import { motion, AnimatePresence } from "framer-motion";
import { Case } from "@/types/workflow";
import { cn } from "@/lib/utils";
import { CaseDialogs } from "../CaseDialogs";
import { CaseHeaderSection } from "../sections/CaseHeaderSection";
import { CaseContentSection } from "../sections/CaseContentSection";
import { EmptyStateSection } from "../EmptyStateSection";
import { LoadingStateSection } from "../LoadingStateSection";
import { OnboardingTour } from "../components/OnboardingTour";
import { EnhancedSearch } from "../components/EnhancedSearch";

interface WebCasesContentProps {
  selectedCase: Case | null;
  createDialogOpen: boolean;
  editDialogOpen: boolean;
  deleteDialogOpen: boolean;
  searchTerm: string;
  activeFilters: string[];
  filteredCases: Case[];
  isLoading: boolean;
  error: Error | null;
  onCreateCase: () => void;
  onEditDialogChange: (open: boolean) => void;
  onCreateDialogChange: (open: boolean) => void;
  onDeleteDialogChange: (open: boolean) => void;
  onSearchChange: (value: string) => void;
  onFilterChange: (filters: string[]) => void;
  onDataRefresh: () => Promise<void>;
  onEditSubmit: (values: any) => Promise<void>;
  onCreateSuccess: () => Promise<void>;
  onDeleteConfirm: () => Promise<void>;
  setSelectedCase: (caseData: Case | null) => void;
}

export function WebCasesContent({
  selectedCase,
  createDialogOpen,
  editDialogOpen,
  deleteDialogOpen,
  searchTerm,
  activeFilters,
  filteredCases,
  isLoading,
  error,
  onCreateCase,
  onEditDialogChange,
  onCreateDialogChange,
  onDeleteDialogChange,
  onSearchChange,
  onFilterChange,
  onDataRefresh,
  onEditSubmit,
  onCreateSuccess,
  onDeleteConfirm,
  setSelectedCase,
}: WebCasesContentProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn(
        "flex-1 h-[100dvh] overflow-hidden",
        "transition-all duration-300 ease-in-out relative",
        "w-full"
      )}
    >
      <OnboardingTour />
      
      <motion.div
        className={cn(
          "h-full flex flex-col overflow-hidden p-4 sm:p-6 gap-4",
          "transition-all duration-300 ease-in-out"
        )}
      >
        <CaseHeaderSection
          onCreateCase={onCreateCase}
          onRefresh={onDataRefresh}
        />

        <EnhancedSearch
          onSearchChange={onSearchChange}
          onFilterChange={onFilterChange}
          cases={filteredCases}
        />

        <AnimatePresence mode="wait">
          {isLoading ? (
            <LoadingStateSection />
          ) : error ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-4"
            >
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                Det oppstod en feil ved lasting av saker: {error.message}
              </div>
            </motion.div>
          ) : filteredCases.length === 0 ? (
            <EmptyStateSection onCreateCase={onCreateCase} />
          ) : (
            <CaseContentSection
              selectedCase={selectedCase}
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              filteredCases={filteredCases}
              isLoading={isLoading}
              error={error}
              onEdit={(caseData) => {
                setSelectedCase(caseData);
                onEditDialogChange(true);
              }}
              onDelete={(caseData) => {
                setSelectedCase(caseData);
                onDeleteDialogChange(true);
              }}
              onViewWorkflow={setSelectedCase}
              onWorkflowUpdate={onDataRefresh}
              onBack={() => setSelectedCase(null)}
            />
          )}
        </AnimatePresence>

        <CaseDialogs
          selectedCase={selectedCase}
          createDialogOpen={createDialogOpen}
          editDialogOpen={editDialogOpen}
          deleteDialogOpen={deleteDialogOpen}
          onCreateDialogChange={onCreateDialogChange}
          onEditDialogChange={onEditDialogChange}
          onDeleteDialogChange={onDeleteDialogChange}
          onEditSubmit={onEditSubmit}
          onCreateSuccess={onCreateSuccess}
          onDeleteConfirm={onDeleteConfirm}
        />
      </motion.div>
    </motion.div>
  );
}